import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, FileSpreadsheet, Download, Check, AlertTriangle, Folder, FolderOpen, ListTodo } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BACKEND_URL, getApiUrl } from '../utils/api';

const API = BACKEND_URL;

const ImportModal = ({ isOpen, onClose, currentUser, onImportComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('upload'); // upload, preview, importing, done
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${getApiUrl()}/import/preview?user_id=${currentUser.id}`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPreviewData(data);
        setStep('preview');
      } else {
        setError(data.detail || t('import.readError', 'Fehler beim Lesen der Datei'));
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError(t('import.uploadError', 'Fehler beim Hochladen der Datei'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!previewData) return;

    setLoading(true);
    setStep('importing');
    setError(null);

    try {
      const response = await fetch(`${getApiUrl()}/import/execute?user_id=${currentUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projects: previewData.projects,
          tasks: previewData.tasks,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setImportResult(data);
        setStep('done');
        if (onImportComplete) {
          onImportComplete(data);
        }
      } else {
        setError(data.detail || t('import.error', 'Import error'));
        setStep('preview');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(t('import.error', 'Import error'));
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  // Direkter Download-Link - umgeht Client-Side Router
  const templateDownloadUrl = `${getApiUrl()}/import/template`;

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-bold">{t('import.title', 'Import aus Excel/CSV')}</h2>
                <p className="text-sm text-emerald-100">{t('import.subtitle', 'Projekte und Tasks importieren')}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Upload Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-all"
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">
                  {t('import.dropzone', 'Datei hierher ziehen oder klicken')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {t('import.fileTypes', 'Excel (.xlsx, .xls) oder CSV Dateien')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 text-emerald-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                  <span>{t('import.analyzing', 'Datei wird analysiert...')}</span>
                </div>
              )}

              {/* Template Download */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-800 mb-2">{t('import.useTemplate', 'Vorlage verwenden')}</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {t('import.templateDesc', 'Laden Sie unsere Excel-Vorlage herunter, um das richtige Format zu verwenden.')}
                </p>
                <a
                  href={templateDownloadUrl}
                  download="Babelmix_Import_Vorlage.xlsx"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {t('import.downloadTemplate', 'Vorlage herunterladen')}
                </a>
              </div>

              {/* Format Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-medium text-blue-800 mb-2">{t('import.supportedColumns', 'Unterstützte Spalten')}</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                  <div><Badge variant="secondary">{t('import.col.project', 'Projekt')}</Badge> {t('import.col.projectDesc', 'Hauptprojekt-Name')}</div>
                  <div><Badge variant="secondary">{t('import.col.subProject', 'Sub-Projekt')}</Badge> {t('import.col.subProjectDesc', 'Etage/Bereich')}</div>
                  <div><Badge variant="secondary">{t('import.col.title', 'Titel')}</Badge> {t('import.col.titleDesc', 'Task-Name')} *</div>
                  <div><Badge variant="secondary">{t('import.col.description', 'Beschreibung')}</Badge> {t('import.col.descriptionDesc', 'Details')}</div>
                  <div><Badge variant="secondary">{t('import.col.priority', 'Priorität')}</Badge> {t('import.col.priorityDesc', 'hoch/normal/niedrig')}</div>
                  <div><Badge variant="secondary">{t('import.col.tags', 'Tags')}</Badge> {t('import.col.tagsDesc', 'Kategorien')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && previewData && (
            <div className="space-y-6">
              {/* File Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FileSpreadsheet className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="font-medium">{previewData.filename}</p>
                  <p className="text-sm text-gray-500">{previewData.total_rows} {t('import.rowsFound', 'Zeilen gefunden')}</p>
                </div>
              </div>

              {/* Warnings */}
              {previewData.warnings?.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-1">{t('import.warnings', 'Hinweise')}:</h4>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {previewData.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <Folder className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                  <p className="text-2xl font-bold text-emerald-700">{previewData.projects?.length || 0}</p>
                  <p className="text-sm text-emerald-600">{t('import.projects', 'Projekte')}</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 text-center">
                  <FolderOpen className="w-8 h-8 mx-auto text-teal-600 mb-2" />
                  <p className="text-2xl font-bold text-teal-700">
                    {previewData.projects?.reduce((sum, p) => sum + (p.sub_projects?.length || 0), 0) || 0}
                  </p>
                  <p className="text-sm text-teal-600">{t('import.subProjects', 'Sub-Projekte')}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <ListTodo className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{previewData.tasks?.length || 0}</p>
                  <p className="text-sm text-blue-600">Tasks</p>
                </div>
              </div>

              {/* Projects Preview */}
              {previewData.projects?.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-800 mb-2">{t('import.projectsLabel', 'Projekte')}:</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {previewData.projects.map((proj, i) => (
                      <div key={i} className="p-2 bg-gray-50 rounded">
                        <p className="font-medium">{proj.name}</p>
                        {proj.sub_projects?.length > 0 && (
                          <p className="text-sm text-gray-500">
                            → {proj.sub_projects.join(', ')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tasks Preview */}
              {previewData.tasks?.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-800 mb-2">{t('import.tasksPreview', 'Tasks (erste 5)')}:</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {previewData.tasks.slice(0, 5).map((task, i) => (
                      <div key={i} className="p-2 bg-gray-50 rounded flex items-start justify-between">
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-xs text-gray-500">
                            {task.project} {task.sub_project && `→ ${task.sub_project}`}
                          </p>
                        </div>
                        <Badge variant={
                          task.priority === 'high' ? 'destructive' :
                          task.priority === 'low' ? 'secondary' : 'default'
                        }>
                          {task.priority === 'high' ? t('tasks.high', 'Hoch') :
                           task.priority === 'low' ? t('tasks.low', 'Niedrig') : t('tasks.medium', 'Normal')}
                        </Badge>
                      </div>
                    ))}
                    {previewData.tasks.length > 5 && (
                      <p className="text-sm text-gray-500 text-center">
                        {t('import.andMore', '... und {{count}} weitere').replace('{{count}}', previewData.tasks.length - 5)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Importing */}
          {step === 'importing' && (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mx-auto mb-6"></div>
              <p className="text-lg font-medium text-gray-700">{t('import.importing', 'Import wird durchgeführt...')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('import.creatingItems', 'Projekte und Tasks werden erstellt')}</p>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && importResult && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('import.success', 'Import erfolgreich!')}</h3>
              
              <div className="grid grid-cols-3 gap-4 my-6">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-emerald-700">{importResult.projects_created}</p>
                  <p className="text-sm text-emerald-600">{t('import.projectsCreated', 'Projekte erstellt')}</p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-teal-700">{importResult.sub_projects_created}</p>
                  <p className="text-sm text-teal-600">{t('import.subProjectsCreated', 'Sub-Projekte erstellt')}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-blue-700">{importResult.tasks_created}</p>
                  <p className="text-sm text-blue-600">{t('import.tasksCreated', 'Tasks erstellt')}</p>
                </div>
              </div>

              {importResult.errors?.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                  <h4 className="font-medium text-yellow-800 mb-1">{t('import.warnings', 'Hinweise')}:</h4>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between">
          {step === 'upload' && (
            <Button variant="outline" onClick={handleClose}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
          )}
          
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => { setStep('upload'); setFile(null); setPreviewData(null); }}>
                {t('import.chooseOther', 'Andere Datei wählen')}
              </Button>
              <Button
                onClick={handleImport}
                disabled={loading || !previewData?.tasks?.length}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? t('import.importingBtn', 'Importiere...') : t('import.importTasks', '{{count}} Tasks importieren').replace('{{count}}', previewData?.tasks?.length || 0)}
              </Button>
            </>
          )}

          {step === 'done' && (
            <Button onClick={handleClose} className="ml-auto bg-emerald-600 hover:bg-emerald-700">
              {t('common.close', 'Schließen')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
